// Landing Page - Medical Records Section v2
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Home, User, Heart, TrendingUp, Shield, Users, Sparkles, Download, CheckCircle2 } from "lucide-react";
import androidPhoneMockup from "@/assets/android-phone-mockup.png";
import petmatchHero from "@/assets/petmatch-hero.png";
import serviceProvidersTeaser from "@/assets/service-providers-teaser.png";
import { OptimizedImage } from '@/components/ui/optimized-image';
import { ArrowRight, Calendar, Image, MessageCircle, Check, FileText, Syringe, Upload, Share2, PawPrint } from 'lucide-react';
import petiusLogo from "@/assets/petius-logo.png";
import { SocialNetworkIcon } from '@/components/ui/social-network-icon';
import medicalRecordsMockup from '@/assets/medical-records-mockup.png';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Navbar from '../components/Navbar';
import LanguageSelector from '../components/LanguageSelector';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from 'react-i18next';
import { useFeaturedMemories } from '@/hooks/usePosts';
import { Skeleton } from '@/components/ui/skeleton';
import { isNativePlatform } from '@/services/platformService';

const Index = () => {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const {
    t,
    i18n
  } = useTranslation();

  // Platform detection for dynamic pricing
  const isNative = isNativePlatform();

  // Fetch dynamic featured memories
  const { data: featuredMemoriesData, isLoading: isLoadingMemories } = useFeaturedMemories(6);

  // Ensure translations are loaded
  useEffect(() => {
    if (!i18n.isInitialized) {
      i18n.init();
    }
  }, [i18n]);

  // Auto-redirect authenticated users to their feed
  useEffect(() => {
    if (user) {
      navigate('/feed');
    }

    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [user, navigate]);

  // Fallback featured memories for hero section mockup (static)
  const heroMockupMemories = [{
    petName: "Max",
    image: "/lovable-uploads/8b08f8de-3fd5-4381-ad11-ae477965901f.png",
    likes: 253,
  }, {
    petName: "Luna",
    image: "/lovable-uploads/9545ce6e-f0bd-4875-ad96-87f4b17c3ce8.png",
    likes: 189,
  }, {
    petName: "Charlie",
    image: "/lovable-uploads/7ad11708-700d-4785-a317-a26d3ffa7478.png",
    likes: 312,
  }, {
    petName: "Bella",
    image: "/images/bella-horse.jpg",
    likes: 217,
  }];

  // Pet categories for promotional section
  const petsLabel = t('categories.pets', 'pets');
  const petCategories = [{
    id: 'dogs',
    name: t('categories.dogs', 'Dogs'),
    count: `15.2K ${petsLabel}`,
    image: '/images/category-dogs.png'
  }, {
    id: 'cats',
    name: t('categories.cats', 'Cats'),
    count: `12.8K ${petsLabel}`,
    image: '/images/category-cats.png'
  }, {
    id: 'birds',
    name: t('categories.birds', 'Birds'),
    count: `5.4K ${petsLabel}`,
    image: '/images/category-birds-parrots.png'
  }, {
    id: 'small_pets',
    name: t('categories.smallPets', 'Small Pets'),
    count: `3.1K ${petsLabel}`,
    image: '/images/category-small-pets.png'
  }, {
    id: 'fish',
    name: t('categories.fish', 'Fish'),
    count: `8.7K ${petsLabel}`,
    image: '/images/category-fish.png'
  }, {
    id: 'reptiles',
    name: t('categories.reptiles', 'Reptiles'),
    count: `2.9K ${petsLabel}`,
    image: '/images/category-reptiles.png'
  }, {
    id: 'horses',
    name: t('categories.horses', 'Horses'),
    count: `4.2K ${petsLabel}`,
    image: '/images/category-horses.png'
  }, {
    id: 'farm_animals',
    name: t('categories.farmAnimals', 'Farm Animals'),
    count: `6.5K ${petsLabel}`,
    image: '/images/category-farm-animals.png'
  }];

  // Core features with icons - now using translation keys
  const coreFeatures = [{
    icon: <PawPrint size={32} className="text-white" />,
    iconBg: "bg-pet-purple",
    title: t('footer.petProfiles'),
    description: t('landing.petProfilesDescription')
  }, {
    icon: <Heart size={32} className="text-white" />,
    iconBg: "bg-pet-pink",
    title: t('footer.memoryTimeline'),
    description: t('landing.memoryTimelineDescription')
  }, {
    icon: <SocialNetworkIcon size={32} />,
    iconBg: "bg-gradient-to-br from-cyan-400 via-green-400 to-yellow-400",
    title: t('landing.socialNetworkTitle'),
    description: t('landing.socialNetworkDescription')
  }];

  // Helper function for platform-aware pricing
  const getPrice = (webPrice: string, nativePrice: string) =>
    isNative ? nativePrice : webPrice;

  // Updated subscription plans using dynamic pricing based on platform
  const subscriptionPlans = [{
    name: t('plans.free.name'),
    monthlyPrice: "Free",
    annualPrice: "Free",
    color: "bg-badge-blue-bg text-badge-blue-text",
    features: t('plans.free.features', {
      returnObjects: true
    })
  }, {
    name: t('plans.standard.name'),
    monthlyPrice: getPrice("US$6.99", "US$11.99"),
    annualPrice: getPrice("US$76.99", "US$98.99"),
    color: "bg-badge-green-bg text-badge-green-text",
    features: t('plans.standard.features', {
      returnObjects: true
    })
  }, {
    name: t('plans.premium.name'),
    monthlyPrice: getPrice("US$9.99", "US$14.99"),
    annualPrice: getPrice("US$109.89", "US$164.99"),
    color: "bg-purple-100 text-purple-600",
    popular: true,
    features: t('plans.premium.features', {
      returnObjects: true
    })
  }, {
    name: t('plans.ultimate.name'),
    monthlyPrice: getPrice("US$19.99", "US$28.99"),
    annualPrice: getPrice("US$219.89", "US$319.99"),
    color: "bg-indigo-100 text-indigo-600",
    features: t('plans.ultimate.features', {
      returnObjects: true
    })
  }];
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-50">
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 border-4 border-pet-purple border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-lg text-gray-600">{t('common.loading')}</p>
      </div>
    </div>;
  }
  return <div className="min-h-screen bg-white overflow-x-hidden">
    {/* Updated Navbar with language selector fix */}
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="safe-area-top android-header-safe">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img src={petiusLogo} alt="Petius" className="w-8 h-8 rounded-full object-cover" />
              <span className="text-xl petius-brand">Petius</span>
            </Link>

            {/* Desktop Navigation - Updated with footer links */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/about" className="text-gray-600 hover:text-gray-900 font-medium">
                {t('footer.aboutUs')}
              </Link>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900 font-medium">
                {t('footer.contact')}
              </Link>
              <Link to="/feedback" className="text-gray-600 hover:text-gray-900 font-medium">
                {t('footer.feedback')}
              </Link>
              {!isNative && (
                <Link to="/donate" className="flex items-center gap-1 text-pink-600 hover:text-pink-700 font-medium">
                  <Heart className="h-4 w-4" />
                  {t('footer.donate')}
                </Link>
              )}
            </div>

            {/* Right side - Language selector and Auth - Fixed to show only language names */}
            <div className="flex items-center space-x-4">
              <LanguageSelector variant="compact" className="w-auto" />

              {user ? <Link to="/feed">
                <Button>Dashboard</Button>
              </Link> : <Link to="/auth">
                <Button>{t('common.signIn')}</Button>
              </Link>}
            </div>
          </div>
        </div>
      </div>
    </nav>

    {/* Hero Section */}
    <section className="bg-gradient-to-br from-[#EEF2FF] via-[#F3E8FF] to-[#FCE7F3] pt-16 pb-24 lg:pt-20 lg:pb-32">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          <div className="lg:w-1/2 space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight">
              {t('landing.heroTitlePart1')} <span className="text-gradient-beloved">{t('landing.heroTitleBelovedPets')}</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-lg">
              {t('landing.heroSubtitle').split('PetMatch™')[0]}
              <span className="font-bold text-pet-purple">PetMatch™</span>
              {t('landing.heroSubtitle').split('PetMatch™')[1]}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/auth" className="w-full sm:w-auto">
                <Button size="lg" className="w-full bg-pet-purple hover:bg-pet-purple/90">
                  {t('common.getStarted')}
                </Button>
              </Link>

              {!isNative && (
                <Link to="/donate" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-2 border-pet-pink text-pet-pink hover:bg-pet-pink/10 font-semibold"
                  >
                    <Heart className="h-5 w-5 mr-2 fill-pet-pink" />
                    {t('landing.supportUs') || 'Support Us ❤️'}
                  </Button>
                </Link>
              )}
            </div>

            {/* App Store & Play Store Badges - hidden in native apps */}
            {!isNative && (
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <a href="https://apps.apple.com/us/app/petius/id6757991417" target="_blank" rel="noopener noreferrer" className="h-[40px] flex items-center">
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download on the App Store"
                    className="h-[40px]"
                  />
                </a>
                <a href="https://play.google.com/store/apps/details?id=io.petius.app" target="_blank" rel="noopener noreferrer" className="h-[40px] flex items-center">
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    className="h-[58px]"
                  />
                </a>
              </div>
            )}
          </div>

          {/* App preview mockup */}
          <div className="lg:w-1/2 relative">
            <div className="bg-white p-3 rounded-2xl shadow-2xl relative z-10 animate-float">
              <div className="bg-gray-50 rounded-xl p-2">
                <div className="flex justify-between items-center mb-3 px-2">
                  <div className="flex items-center gap-2">
                    <img src={petiusLogo} alt="Petius" className="w-8 h-8 rounded-full object-cover" />
                    <span className="font-medium text-sm petius-brand">Petius Feed</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-xs bg-badge-blue-bg text-badge-blue-text px-2 py-0.5 rounded-full">{t('landing.popular')}</span>
                    <span className="text-xs bg-badge-green-bg text-badge-green-text px-2 py-0.5 rounded-full">{t('landing.new')}</span>
                  </div>
                </div>

                {/* App content mockup */}
                <div className="grid grid-cols-2 gap-2">
                  {heroMockupMemories.map((memory, index) => <div key={index} className="rounded-lg overflow-hidden relative">
                    <img src={memory.image} alt={memory.petName} className="w-full aspect-square object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                      <p className="text-white text-xs font-medium">{memory.petName}</p>
                      <div className="flex items-center gap-1 text-white/80 text-xs">
                        <Heart className="h-3 w-3" />
                        <span>{memory.likes}</span>
                      </div>
                    </div>
                  </div>)}
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-pet-pink/20 rounded-full blur-xl -z-10"></div>
            <div className="absolute -top-10 -left-10 w-24 h-24 bg-pet-blue/20 rounded-full blur-lg -z-10"></div>
          </div>
        </div>
      </div>
    </section>

    {/* Pet Categories Section - Promotional */}
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('landing.discoverCategories')}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('landing.findSimilarPets')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {petCategories.map((category, index) => <div key={category.id} className="relative rounded-xl overflow-hidden">
            <OptimizedImage src={category.image} alt={category.name} className="w-full aspect-square object-cover" width="270" height="270" sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw" />
            <div className="absolute bottom-4 left-4 right-4">
              <h3 className="text-white text-xl font-bold drop-shadow-lg">{category.name}</h3>
              <p className="text-white/90 text-sm drop-shadow-md">{category.count}</p>
            </div>
          </div>)}
        </div>

        <div className="text-center mt-12">
          <Link to="/auth">
            <Button size="lg" className="bg-pet-purple hover:bg-pet-purple/90">
              {t('landing.joinToExplore')}
            </Button>
          </Link>
        </div>
      </div>
    </section>

    {/* Featured Memories Section - Dynamic */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('landing.featuredMemories')}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('landing.todaySuperstars')}</p>
        </div>

        {isLoadingMemories ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <Skeleton className="w-full aspect-square" />
                <div className="p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-6 w-6 rounded-full" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-4 w-10" />
                      <Skeleton className="h-4 w-10" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : featuredMemoriesData && featuredMemoriesData.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredMemoriesData.map((post) => {
              const mediaUrl = post.media?.[0]?.url;
              const petName = post.pet?.name || 'Pet';

              return (
                <div key={post.id} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <div className="relative">
                    {mediaUrl ? (
                      <OptimizedImage
                        src={mediaUrl}
                        alt={petName}
                        className="w-full aspect-square object-cover"
                        width="400"
                        height="400"
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-gray-200 flex items-center justify-center">
                        <PawPrint className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium truncate">{petName}</h3>
                      <span className="text-xs bg-gradient-to-r from-pet-purple/10 to-pet-pink/10 text-pet-purple px-2 py-0.5 rounded-full font-medium">
                        {t('landing.featured')}
                      </span>
                    </div>

                    {post.caption && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{post.caption}</p>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={post.pet?.photo_url} />
                          <AvatarFallback>{petName.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-gray-600 truncate max-w-[120px]">
                          {post.pet?.species || 'Pet'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-gray-500">
                          <Heart className="h-4 w-4" />
                          <span className="text-xs">{post.likes_count || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <MessageCircle className="h-4 w-4" />
                          <span className="text-xs">{post.comments_count || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <PawPrint className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('landing.noMemoriesYet')}</p>
          </div>
        )}

        <div className="text-center mt-12">
          <Link to="/auth">
            <Button size="lg" className="bg-pet-purple hover:bg-pet-purple/90">
              {t('landing.joinToSeeMore')}
            </Button>
          </Link>
        </div>
      </div>
    </section>

    {/* Core Features Section */}
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('landing.everythingYouNeed')}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('landing.designedForParents')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {coreFeatures.map((feature, index) => <div key={index} className="flex flex-col items-center text-center px-4">
            <div className={`${feature.iconBg} w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-lg`}>
              {feature.icon}
            </div>
            <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
            <p className="text-gray-600">{feature.description}</p>
          </div>)}
        </div>
      </div>
    </section>

    {/* Medical Records Showcase Section */}
    <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left: Content */}
          <div className="lg:w-1/2 space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('landing.medicalRecordsTitle')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl">
              {t('landing.medicalRecordsSubtitle')}
            </p>

            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="bg-pet-purple/10 p-2 rounded-lg mt-1">
                  <FileText className="h-5 w-5 text-pet-purple" />
                </div>
                <span className="text-gray-700">{t('landing.medicalRecordsFeature1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-pet-blue/10 p-2 rounded-lg mt-1">
                  <Syringe className="h-5 w-5 text-pet-blue" />
                </div>
                <span className="text-gray-700">{t('landing.medicalRecordsFeature2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-pet-pink/10 p-2 rounded-lg mt-1">
                  <Calendar className="h-5 w-5 text-pet-pink" />
                </div>
                <span className="text-gray-700">{t('landing.medicalRecordsFeature3')}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-lg mt-1">
                  <Upload className="h-5 w-5 text-green-600" />
                </div>
                <span className="text-gray-700">{t('landing.medicalRecordsFeature4')}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-lg mt-1">
                  <Share2 className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-gray-700">{t('landing.medicalRecordsFeature5')}</span>
              </li>
            </ul>

            <Link to="/auth">
              <Button size="lg" className="bg-pet-purple hover:bg-pet-purple/90 mt-4">
                {t('landing.exploreMedicalRecords')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

          {/* Right: Visual Mockup */}
          <div className="lg:w-1/2">
            <div className="relative">
              <img src={medicalRecordsMockup} alt="Medical Records Dashboard" className="rounded-2xl shadow-2xl w-full" />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-pet-purple/20 rounded-full blur-2xl -z-10"></div>
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-pet-blue/20 rounded-full blur-xl -z-10"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* PetMatch™ Section - Simplified */}
    <section className="py-20 bg-gradient-to-br from-pink-50 via-purple-50 to-orange-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-40 h-40 bg-pet-pink/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-48 h-48 bg-pet-purple/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-56 h-56 bg-pet-orange/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-gradient-to-r from-pet-pink to-pet-purple text-white px-6 py-2 text-base font-semibold mb-4">
            <Heart className="w-4 h-4 mr-2 inline" />
            {t('landing.petMatch.badge')}
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-pet-pink via-pet-purple to-pet-orange bg-clip-text text-transparent">
            {t('landing.petMatch.title')}
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-6">
            {t('landing.petMatch.subtitle')}
          </p>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('landing.petMatch.description')}
          </p>
        </div>

        {/* Hero Image */}
        <div className="mb-12 flex justify-center">
          <div className="relative max-w-2xl">
            <img src={petmatchHero} alt="PetMatch™ - Find love for your pets" className="rounded-3xl shadow-2xl w-full hover-scale transition-transform duration-500" />
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4 animate-pulse">
              <div className="flex items-center gap-2">
                <Heart className="w-6 h-6 text-pet-pink fill-current" />
                <span className="font-bold text-2xl text-pet-purple">PetMatch™</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/pet-matching-info">
            <Button size="lg" className="bg-gradient-to-r from-pet-pink via-pet-purple to-pet-orange hover:opacity-90 text-white text-xl px-12 py-6 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300">
              <Heart className="mr-2 h-6 w-6" />
              {t('landing.petMatch.ctaButton')}
            </Button>
          </Link>
        </div>
      </div>
    </section>

    {/* Service Providers Teaser */}
    <section className="py-16 bg-gradient-to-r from-pet-orange via-pet-pink to-pet-purple relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-20">
        <img src={serviceProvidersTeaser} alt="" className="w-full h-full object-cover" />
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

          {/* Left: Headline + Description */}
          <div className="space-y-4">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-2">
              <span className="text-white font-semibold text-sm">
                🏢 {t('landing.providers.teaserBadge')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              <span className="text-white whitespace-pre-line block mb-2">
                {t('landing.providers.teaserTitleHighlight')}
              </span>
              {t('landing.providers.teaserTitle')}
            </h2>
            <p className="text-lg text-white/90">
              {t('landing.providers.teaserSubtitle')}
            </p>

            {/* Key Stats - Compact */}
            <div className="flex gap-4 pt-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 flex-1">
                <div className="text-2xl font-bold text-white">{t('landing.providers.teaserStat1Value')}</div>
                <div className="text-sm text-white/80">{t('landing.providers.teaserStat1')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 flex-1">
                <div className="text-lg font-bold text-white">{t('landing.providers.teaserStat2Value')}</div>
                <div className="text-sm text-white/80">{t('landing.providers.teaserStat2')}</div>
              </div>
            </div>
          </div>

          {/* Right: Benefits + CTA */}
          <div className="space-y-6">
            {/* 5 Quick Benefits */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">🎯</span>
                </div>
                <span className="text-base font-medium">{t('landing.providers.teaserBenefit1')}</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">✅</span>
                </div>
                <span className="text-base font-medium">{t('landing.providers.teaserBenefit2')}</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">📈</span>
                </div>
                <span className="text-base font-medium">{t('landing.providers.teaserBenefit3')}</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">🎖️</span>
                </div>
                <span className="text-base font-medium">{t('landing.providers.teaserBenefit4')}</span>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">💰</span>
                </div>
                <span className="text-base font-medium">{t('landing.providers.teaserBenefit5')}</span>
              </div>
            </div>

            {/* CTA Button */}
            <div className="pt-2">
              <Link to="/service-providers">
                <Button size="lg" className="w-full bg-white text-pet-purple hover:bg-white/90 font-bold text-lg py-6 shadow-xl">
                  {t('landing.providers.teaserCta')} →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Subscription Plans */}
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('landing.choosePerfectPlan')}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('landing.flexibleOptions')}</p>
        </div>

        <Tabs defaultValue="monthly" onValueChange={value => setBillingCycle(value as 'monthly' | 'annual')} className="w-full">
          <div className="flex justify-center mb-6">
            <TabsList className="bg-gray-100 rounded-lg p-1 w-full max-w-2xl">
              <div className="grid w-full grid-cols-2 gap-2 md:gap-3">
                <TabsTrigger value="monthly" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pet-purple data-[state=active]:to-pet-blue data-[state=active]:text-white data-[state=inactive]:text-gray-600 rounded-md px-3 py-3 font-medium text-sm min-h-[3rem] flex items-center justify-center">
                  <span className="text-center leading-tight">{t('landing.monthlyBilling')}</span>
                </TabsTrigger>
                <TabsTrigger value="annual" className="relative data-[state=active]:bg-gradient-to-r data-[state=active]:from-pet-purple data-[state=active]:to-pet-blue data-[state=active]:text-white data-[state=inactive]:text-gray-600 rounded-md px-3 py-3 pr-8 font-medium text-sm min-h-[3rem] flex items-center justify-center">
                  <span className="text-center leading-tight">{t('landing.annualBilling')}</span>
                  <span className="absolute -top-1 -right-1 text-xs px-2 py-0.5 bg-badge-green-dark text-white rounded-full font-semibold whitespace-nowrap">
                    {t('landing.saveUp')}
                  </span>
                </TabsTrigger>
              </div>
            </TabsList>
          </div>

          <TabsContent value="monthly" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
              {subscriptionPlans.map((plan, index) => <div key={index} className={`bg-white rounded-xl border ${plan.popular ? 'border-pet-purple shadow-lg' : 'border-gray-200 shadow-sm'} overflow-hidden relative`}>
                {plan.popular && <div className="absolute top-0 left-0 right-0 bg-pet-purple text-white text-xs py-1 text-center font-medium">
                  {t('landing.mostPopular')}
                </div>}
                <div className={`${plan.color} py-6 px-8 ${plan.popular ? 'pt-8' : ''}`}>
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">{plan.monthlyPrice}</span>
                    {plan.monthlyPrice !== t('plans.free.monthlyPrice') && <span className="text-sm">/{t('landing.month')}</span>}
                  </div>
                </div>

                <div className="px-8 py-6">
                  <ul className="space-y-3">
                    {Array.isArray(plan.features) && plan.features.map((feature, i) => {
                      // Highlight storage capacity numbers, trial days, and PetMatch™
                      const highlightedFeature = feature
                        .replace(/(\d+\s*(?:MB|GB))/gi, '<span class="font-bold text-pet-purple text-base">$1</span>')
                        .replace(/(15(?:-day|天|日間| días| jours| giorni| dias))/gi, '<span class="font-bold text-pet-purple text-base">$1</span>')
                        .replace(/(PetMatch™)/gi, '<span class="font-bold text-pet-purple text-base">$1</span>');

                      return (
                        <li key={i} className="flex items-center gap-2">
                          <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="text-sm" dangerouslySetInnerHTML={{ __html: highlightedFeature }} />
                        </li>
                      );
                    })}
                  </ul>

                  <div className="mt-8">
                    <Link to="/auth" className="block">
                      <Button className={`w-full ${plan.popular ? 'bg-pet-purple hover:bg-pet-purple/90' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`} variant={plan.popular ? 'default' : 'outline'}>
                        {plan.monthlyPrice === t('plans.free.monthlyPrice') ? t('common.getStarted') : t('landing.choosePlan')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>)}
            </div>
          </TabsContent>

          <TabsContent value="annual" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
              {subscriptionPlans.map((plan, index) => <div key={index} className={`bg-white rounded-xl border ${plan.popular ? 'border-pet-purple shadow-lg' : 'border-gray-200 shadow-sm'} overflow-hidden relative`}>
                {plan.popular && <div className="absolute top-0 left-0 right-0 bg-pet-purple text-white text-xs py-1 text-center font-medium">
                  {t('landing.mostPopular')}
                </div>}
                <div className={`${plan.color} py-6 px-8 ${plan.popular ? 'pt-8' : ''}`}>
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">{plan.annualPrice}</span>
                    {plan.annualPrice !== t('plans.free.annualPrice') && <span className="text-sm">/{t('landing.year')}</span>}
                  </div>
                </div>

                <div className="px-8 py-6">
                  <ul className="space-y-3">
                    {Array.isArray(plan.features) && plan.features.map((feature, i) => {
                      // Highlight storage capacity numbers and trial days
                      const highlightedFeature = feature
                        .replace(/(\d+\s*(?:MB|GB))/gi, '<span class="font-bold text-pet-purple text-base">$1</span>')
                        .replace(/(15(?:-day|天|日間| días| jours| giorni| dias))/gi, '<span class="font-bold text-pet-purple text-base">$1</span>');

                      return (
                        <li key={i} className="flex items-center gap-2">
                          <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="text-sm" dangerouslySetInnerHTML={{ __html: highlightedFeature }} />
                        </li>
                      );
                    })}
                  </ul>

                  <div className="mt-8">
                    <Link to="/auth" className="block">
                      <Button className={`w-full ${plan.popular ? 'bg-pet-purple hover:bg-pet-purple/90' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`} variant={plan.popular ? 'default' : 'outline'}>
                        {plan.annualPrice === t('plans.free.annualPrice') ? t('common.getStarted') : t('landing.choosePlan')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>)}
            </div>
          </TabsContent>
        </Tabs>

        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg text-sm mt-8">
          <p className="font-medium mb-2">{t('subscription.subscriptionNotes')}</p>
          <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
            <li>{t('subscription.allPlansInclude')}</li>
            <li>{t('subscription.storageLimitsNote')}</li>
            <li>{t('subscription.storageQuotaNote')}</li>
            <li>{t('subscription.cancelAnytime')}</li>
            <li>{t('subscription.premiumFeaturesImmediate')}</li>
            <li>{t('subscription.annualSubscriptions')}</li>
            <li>{t('subscription.supportContact')}</li>
          </ul>
        </div>
      </div>
    </section>

    {/* 🤖 Android Download Section */}
    <section className="py-20 bg-gradient-to-br from-purple-50 via-blue-50 to-orange-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-10 left-10 w-32 h-32 bg-pet-purple/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-pet-orange/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">

          {/* Left side - Phone mockup visual */}
          <div className="lg:w-5/12 flex justify-center">
            <div className="relative animate-fade-in">
              <img src={androidPhoneMockup} alt="Petius Android App" className="w-full max-w-md drop-shadow-2xl hover-scale transition-transform duration-500" />
              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 animate-pulse">
                <Badge className="bg-pet-purple text-white px-4 py-2 text-base shadow-lg border-2 border-white">
                  <Sparkles className="w-4 h-4 mr-1 inline" />
                  {t('landing.androidDownload.badge')}
                </Badge>
              </div>
            </div>
          </div>

          {/* Right side - Content */}
          <div className="lg:w-7/12 space-y-6">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="outline" className="bg-white/80 backdrop-blur border-pet-orange text-pet-orange px-4 py-1.5 text-sm font-semibold">
                {t('landing.androidDownload.newVersionBadge')}
              </Badge>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              {t('landing.androidDownload.title')}
            </h2>

            <p className="text-lg text-gray-600 leading-relaxed">
              {t('landing.androidDownload.subtitle')}
            </p>

            {/* Download button - PayPal styled */}
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <a href="https://petius.io/releases/petius-ver-2.apk" download className="inline-block">
                <Button size="lg" className="bg-gradient-to-r from-pet-purple to-pet-blue hover:from-pet-purple/90 hover:to-pet-blue/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 text-lg px-8 py-6">
                  <Download className="mr-2 h-5 w-5" />
                  {t('landing.androidDownload.downloadButton')}
                </Button>
              </a>
              <div className="flex flex-col text-sm text-gray-500">
                <span>{t('landing.androidDownload.requirements')}</span>
                <span>{t('landing.androidDownload.fileSize')}</span>
              </div>
            </div>

            {/* Purchase & Install Steps */}
            <div className="mt-8 p-6 bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-pet-purple" />
                {t('landing.androidDownload.installationTitle')}
              </h3>
              <ol className="space-y-3">
                <li className="flex items-start gap-3 text-gray-700">
                  <CheckCircle2 className="h-5 w-5 text-pet-blue mt-0.5 flex-shrink-0" />
                  <span>{t('landing.androidDownload.step1')}</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <CheckCircle2 className="h-5 w-5 text-pet-blue mt-0.5 flex-shrink-0" />
                  <span>{t('landing.androidDownload.step2')}</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <CheckCircle2 className="h-5 w-5 text-pet-purple mt-0.5 flex-shrink-0" />
                  <span>{t('landing.androidDownload.step3')}</span>
                </li>
                
                <li className="flex items-start gap-3 text-gray-700">
                  <CheckCircle2 className="h-5 w-5 text-pet-blue mt-0.5 flex-shrink-0" />
                  <span>{t('landing.androidDownload.step4')}</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <CheckCircle2 className="h-5 w-5 text-pet-blue mt-0.5 flex-shrink-0" />
                  <span>{t('landing.androidDownload.step5')}</span>
                </li>
              </ol>

              {/* Security note */}
              <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  {t('landing.androidDownload.securityNote')}
                </p>
                <p className="text-sm text-blue-800">
                  {t('landing.androidDownload.securityDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Community Banner */}
    <section className="bg-gradient-to-r from-pet-purple/90 to-pet-blue/90 py-16">
      <div className="container mx-auto px-4 max-w-7xl text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4">{t('landing.joinCommunity')}</h2>
          <p className="text-white/90 text-lg mb-8">{t('landing.communityDescription')}</p>
          <Link to="/auth">
            <Button size="lg" className="bg-white text-pet-purple hover:bg-gray-100">
              {t('landing.signUpFree')}
            </Button>
          </Link>
        </div>
      </div>
    </section>

    {/* Footer - Updated with better social media links */}
    <footer className="bg-gray-800 text-white py-12">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex flex-col md:flex-row justify-between pb-8 mb-8 border-b border-gray-700">
          <div className="mb-8 md:mb-0 md:w-1/3">
            <div className="flex items-center space-x-2">
              <img src={petiusLogo} alt="Petius" className="w-8 h-8 rounded-full object-cover" />
              <span className="text-xl petius-brand">Petius</span>
            </div>
            <p className="mt-4 text-gray-400 max-w-xs">{t('footer.description')}</p>
            <div className="flex space-x-4 mt-6">
              <a href="https://www.facebook.com/profile.php?id=61581054164585" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" aria-label="Follow us on Facebook">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd"></path>
                </svg>
              </a>
              <a href="https://instagram.com/petiusnetwork" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" aria-label="Follow us on Instagram">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.045-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.08c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.353.137.882.3 1.857.344 1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd"></path>
                </svg>
              </a>
              <a href="https://x.com/PetiusNetwork" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" aria-label="Follow us on X">
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path>
                </svg>
              </a>
            </div>
            {!isNative && (
              <div className="flex items-center gap-3 mt-4 opacity-80 hover:opacity-100 transition-opacity">
                <a href="https://apps.apple.com/us/app/petius/id6757991417" target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download on the App Store"
                    className="h-[30px]"
                  />
                </a>
                <a href="https://play.google.com/store/apps/details?id=io.petius.app" target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    className="h-[45px]"
                  />
                </a>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:w-2/3 ml-auto">
            <div>
              <h3 className="font-semibold mb-4">{t('footer.features')}</h3>
              <ul className="space-y-2">
                <li><Link to="/pet-matching-info" className="text-gray-400 hover:text-white">PetMatch™</Link></li>
                <li><span className="text-gray-400">{t('footer.petProfiles')}</span></li>
                <li><span className="text-gray-400">{t('footer.memoryTimeline')}</span></li>
                <li><span className="text-gray-400">{t('footer.medicalRecords')}</span></li>
                <li><span className="text-gray-400">{t('footer.petCommunity')}</span></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">{t('footer.company')}</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white">{t('footer.aboutUs')}</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white">{t('footer.contact')}</Link></li>
                <li><Link to="/feedback" className="text-gray-400 hover:text-white">{t('footer.feedback')}</Link></li>
                {!isNative && <li><Link to="/donate" className="text-gray-400 hover:text-white">{t('footer.donate')}</Link></li>}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">{t('footer.legal')}</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy-policy" className="text-gray-400 hover:text-white">{t('footer.privacyPolicy')}</Link></li>
                <li><Link to="/terms-of-service" className="text-gray-400 hover:text-white">{t('footer.termsOfService')}</Link></li>
                <li><Link to="/cookie-policy" className="text-gray-400 hover:text-white">{t('footer.cookiePolicy')}</Link></li>
                <li><Link to="/child-safety-policy" className="text-gray-400 hover:text-white">{t('footer.childSafetyPolicy')}</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400">© 2025 Petius. {t('footer.allRightsReserved')}</p>
          <div className="mt-4 md:mt-0">
            <LanguageSelector variant="compact" />
          </div>
        </div>
      </div>
    </footer>
  </div>;
};
export default Index;